<template>
  <v-card class="d-flex flex-column grey lighten-2">
    <template v-if="formVisible">
      <WorkflowForm
        ref="newAssignmentItem"
        v-for="(newWorkflowData, i) in gNewWorkflowsDatas"
        :key="`new-fc-assignment-${newWorkflowData.id}`"
        :datasIndex="i"
        :workflowItem="newWorkflowData"
      />
    </template>
    <v-card-actions class="d-flex justify-end pr-4 pb-4">
      <v-btn
        v-if="!gIsUpdated"
        depressed
        class="mx-3 grey lighten-2"
        :loading="workflowProcessing"
        @click="handleAddingAnotherWorkflow"
      >
        <v-icon>mdi-plus</v-icon>
        Add another workflow
      </v-btn>
      <v-btn
        v-if="!gIsUpdated"
        depressed
        class="mx-3 grey lighten-2"
        :loading="workflowProcessing"
        @click="handleWorkflowDuplication"
      >
        <v-icon>mdi-plus</v-icon>
        Duplicate
      </v-btn>
      <v-scale-transition>
        <div v-if="deleteConfirmation" class="d-flex align-center">
          <h3>Are you sure?</h3>
          <v-btn
            class="ml-2"
            color="success"
            @click="deleteWorkflow"
            :loading="deletionProcessing || workflowProcessing"
          >Yes</v-btn>
          <v-btn
            class="ml-2"
            :loading="deletionProcessing || workflowProcessing"
            color="error"
            @click="deleteConfirmation = false"
          >No</v-btn>
        </div>
      </v-scale-transition>
      <v-btn
        v-if="gIsUpdated"
        depressed
        class="mx-3 grey lighten-2"
        :loading="deletionProcessing || workflowProcessing"
        :disabled="deleteConfirmation"
        @click="handleEditAssignmentCopy"
      >
        <v-icon>mdi-plus</v-icon>
        Add Copy as New
      </v-btn>
      <v-btn
        color="secondary"
        class="mx-3"
        :loading="deletionProcessing || workflowProcessing"
        :disabled="deleteConfirmation"
        @click="createWorkflow"
      >{{ gIsUpdated ? "Update" : "Create" }}</v-btn>
      <v-btn
        color="secondary"
        class="mx-3"
        :loading="deletionProcessing || workflowProcessing"
        @click.prevent="closeWorkflowForm"
      >Close</v-btn>
      <v-btn
        v-if="gIsUpdated"
        class="ml-4"
        height="36px"
        small
        color="secondary"
        :loading="deletionProcessing || workflowProcessing"
        :disabled="deleteConfirmation"
        @click="deleteConfirmation = true"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import WorkflowForm from "./WorkflowForm.vue";
import { prepareDataToCreateWorkflow, prepareDataToUpdateWorkflow } from "../../utils/workflowUtils";
import {
  createNewWorkflow,
  updateWorkflow,
  removeWorkflow,
  getAllProjectsWithWorkflowsData
} from "../../utils/newDbUtils";
export default {
  name: "WorkflowModal",
  components: { WorkflowForm },
  data() {
    return {
      formVisible: true,
      workflowProcessing: false,
      deleteConfirmation: false,
      deletionProcessing: false,
      // Snapshot of store data taken the moment the modal opens
      originalWorkflowDatas: null,
      originalIsUpdated: false,
    };
  },
  computed: {
    ...mapGetters("workflow", ["gWorkflowName", "gWorkflow", "gWorkflowData", "gIsUpdated", "gNewWorkflowsDatas"]),
    ...mapGetters("assignment", ["projectsList", "geoOptions", "requestTypeOptions", "mediaTypeOptions", "keywordOptions"]),
    ...mapGetters("staff", ["gStaffList"]),
  },
  mounted() {
    // Take a deep snapshot the moment the modal is mounted (opened)
    this.originalWorkflowDatas = _.cloneDeep(this.gNewWorkflowsDatas);
    this.originalIsUpdated = this.gIsUpdated;
  },
  methods: {
    ...mapActions("workflow", [
      "aSetWorkflowField",
      "aClearWorkflow",
      "aSetIfUpdated",
      "aRemoveWorkflow",
      "aSetNewWorkflowDatas",
      "aAddAnotherWorkflow",
      "aAddDuplicateWorkflow",
      "aRemoveWorkFlow",
      "aClearNewWorkflowDatas",
      "aAddCopyAsNewWorkflow"
    ]),
    ...mapActions("assignment", ["setCommonData"]),
    ...mapActions("flashMessage", ["handleFlash"]),
    handleAddingAnotherWorkflow() {
      this.aAddAnotherWorkflow();
    },
    handleWorkflowDuplication() {
      let copyItem = _.cloneDeep(_.last(this.gNewWorkflowsDatas));
      copyItem.id = uuidv4();
      this.aAddDuplicateWorkflow(copyItem);
    },
    handleEditAssignmentCopy() {
      let copyItem = _.cloneDeep(_.last(this.gNewWorkflowsDatas));
      copyItem.id = uuidv4();
      copyItem.workflowHistory = [];
      copyItem.groupedWorkflowHistory = [];
      this.aAddCopyAsNewWorkflow(copyItem);
    },
    confirmFormValid() {
      let validator = [];
      this.$refs.newAssignmentItem.forEach((el) => validator.push(el.validateForm()));
      return validator.every((value) => value === true);
    },

    async createWorkflow() {
      if (this.confirmFormValid()) {
        try {
          this.workflowProcessing = true;

          // Flush all local form state into Vuex before reading gNewWorkflowsDatas
          this.$refs.newAssignmentItem.forEach((el) => el.flushLocalToStore());

          if (!this.gIsUpdated) {
            const dataArray = this.gNewWorkflowsDatas.map(prepareDataToCreateWorkflow);
            const responses = await Promise.all(dataArray.map(createNewWorkflow));
            responses.forEach(resp => this.handleFlash({ response: resp, show: true }));
            const resp = await getAllProjectsWithWorkflowsData(this.$route.query);
            resp.data.forEach((project) => {
              project.staff.forEach((staffEl) => {
                let originalStaff = this.gStaffList.find((item) => item.user.id === staffEl.user.id);
                staffEl.adminDetails = originalStaff.adminDetails;
              });
            });
            this.setCommonData({ field: "projectsList", data: resp.data });
            this.$emit("close-expanded-items");
          }

          if (this.gIsUpdated) {
            const data = prepareDataToUpdateWorkflow(this.gNewWorkflowsDatas[0]);
            await updateWorkflow(this.gNewWorkflowsDatas[0].id, data).then(async (resp) => {
              this.handleFlash({ response: resp, show: true });
              const query = { ...this.$route.query };
              this.$router.push({ path: `/workflows`, query }).catch(() => {});
              this.$emit("close-expanded-items");
            });
          }

          this.originalWorkflowDatas = null;
          this.aSetIfUpdated(false);
          this._destroyAndClose();
          this.workflowProcessing = false;
        } catch (error) {
          console.error(error);
          this.workflowProcessing = false;
        }
      } else {
        this.workflowProcessing = false;
      }
    },
    async deleteWorkflow() {
      this.deletionProcessing = true;
      await removeWorkflow(this.gNewWorkflowsDatas[0].id)
        .then(async (resp) => {
          this.handleFlash({ response: resp, show: true });
          const response = await getAllProjectsWithWorkflowsData();
          response.data.forEach((project) => {
            project.staff.forEach((staffEl) => {
              let originalStaff = this.gStaffList.find((item) => item.user.id === staffEl.user.id);
              staffEl.adminDetails = originalStaff.adminDetails;
            });
          });
          this.setCommonData({ field: "projectsList", data: response.data });
          this.$emit("close-expanded-items");
          this.deletionProcessing = false;
          this.deleteConfirmation = false;
          this.originalWorkflowDatas = null;
          this._destroyAndClose();
        })
        .catch((error) => {
          console.error(error);
          this.deletionProcessing = false;
          this.deleteConfirmation = false;
        });
    },

    closeWorkflowForm() {
      if (this.originalWorkflowDatas) {
        this.aSetNewWorkflowDatas(_.cloneDeep(this.originalWorkflowDatas));
        this.aSetIfUpdated(this.originalIsUpdated);
      }
      this._destroyAndClose();
    },

    _destroyAndClose() {
      this.formVisible = false;
      this.aClearNewWorkflowDatas();
      this.$nextTick(() => {
        this.formVisible = true;
        this.$emit("close-form");
      });
    },
  },
};
</script>